import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';

import { AdminCategory } from './../../../../models/admin-category.model';
import { AdminCategoryUpdate } from './../../../../models/admin-category-update.model';
import { CategoryUpdate } from './../../../../models/category-update.model';
import { AdminService } from './../../../../services/site/admin.service';
import { CategoryService } from './../../../../services/site/category.service';
import { ToastrService } from 'ngx-toastr';
import { TokenService } from './../../../../services/auth/token.service';

import { NgxSpinnerService } from "ngx-spinner";
import { environment } from './../../../../../environments/environment';

import { ProductCategoryStatusRequest, Category_v2, Subcategory_v2, SubcategoryUpdate_v2 } from './../../../../models/category.model';
import { Subcategory, SubcategoryFormData } from './../admin-subcategory-modal/admin-subcategory-modal.component';

export class FileInfo
{
  fileName: string;
  fileType: string;
  fileData: string;
}

@Component({
  selector: 'app-admin-category-edit',
  templateUrl: './admin-category-detail.component.html',
  styleUrl: './admin-category-detail.component.scss',
})
export class AdminCategoryDetailComponent implements OnInit {
  categoryForm!: FormGroup;
  category: Category_v2 | null = null;
  subcategories: Subcategory_v2[] = [];
  isEditMode = false;
  isLoading = false;
  isSaving = false;
  error = '';
  success = '';
  categoryId: number | null = null;
  imagePreview: string | null = null;
  imageSource = `${environment.awsUrl}/categories`;
  uploadFile = new FileInfo();

  // Subcategory modal management
  showSubcategoryModal = false;
  editingSubcategory: Subcategory | null = null;
  isEditingSubcategory = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private categoryService: CategoryService,
    private adminService: AdminService,
    private spinner: NgxSpinnerService,
    private readonly toastr: ToastrService,
    private readonly tokenService: TokenService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    if (!this.tokenService.isAdmin()) {
      this.router.navigate(['/home']);
    }
    this.spinner.show();
    setTimeout(() => {
      /** spinner ends after 5 seconds */
      this.spinner.hide();
    }, 3000);
    //this.parentForm = this.createParentForm();
    this.route.params.subscribe(params => {
      console.log('id: ' + params.id);
      this.categoryId = params.id;
      if (this.categoryId == 0) {
        this.isEditMode = false;
        this.setDefaultValues();
      } else {
        this.isEditMode = true;
        this.loadCategory();
      }
      
      //this.parentForm = this.createParentForm();
    });
  }

  private initializeForm(): void {
    this.categoryForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(500)]],
      image: [''],
      activeFlag: [true],
      parentCategoryId: [null],
      sortOrder: [0, [Validators.min(0)]],
      slug: ['', [Validators.required, Validators.pattern(/^[a-z0-9-]+$/)]],
      metaTitle: ['', [Validators.maxLength(60)]],
      metaDescription: ['', [Validators.maxLength(160)]]
    });

    // Auto-generate slug from name
    this.categoryForm.get('name')?.valueChanges.subscribe(name => {
      if (name && !this.isEditMode) {
        const slug = this.generateSlug(name);
        this.categoryForm.patchValue({ slug }, { emitEvent: false });
      }
    });
  }

  private setDefaultValues(): void {
    this.categoryForm.patchValue({
      name: '',
      description: '',
      imagePath: '',
      activeFlag: true,
      parentCategoryId: null,
      sortOrder: 0,
      slug: '',
      metaTitle: '',
      metaDescription: ''
    });
  }

  private loadCategory(): void {
    if (!this.categoryId) return;

    this.isLoading = true;
    this.error = '';

    // Simulate API call
    setTimeout(() => {
      try {
        this.categoryService.getCategory_v2(this.categoryId)
            .subscribe(category => {
                console.log('parent: ' + JSON.stringify(category));
                this.category = category;
                this.categoryService.getSubcategories_v2(this.categoryId!)
                    .subscribe(subcategories => {
                        this.subcategories = subcategories;
                        console.log('subcategories: ' + JSON.stringify(this.subcategories));
                        if (this.category) {
                            this.categoryForm.patchValue({
                                name: this.category.name,
                                description: this.category.description,
                                imagePath: this.category.imagePath,
                                activeFlag: this.category.activeFlag,
                                parentCategoryId: this.category.parentCategoryId,
                                sortOrder: this.category.sortOrder,
                                slug: this.category.slug,
                                metaTitle: this.category.metaTitle,
                                metaDescription: this.category.metaDescription
                            });
                            this.imagePreview = `${this.imageSource}/${this.category.imagePath}`;
                            this.isLoading = false;
                        }
                    });
            });
        
    } catch (error) {
        this.error = 'Failed to load category. Please try again.';
        this.isLoading = false;
      }
    }, 500);
  }

  onSubmit(): void {
    console.log('onSubmit called');
    if (this.categoryForm.valid) {
      console.log('Form Data:', this.categoryForm.value);
      this.isSaving = true;
      this.error = '';
      this.success = '';

      console.log('Category ID:', this.categoryId);
      console.log('Is Edit Mode:', this.isEditMode);
      console.log('category:', this.category);

      const formData = this.categoryForm.value;

      console.log('Form Data before save:', formData);
      const parentData: AdminCategory = {
        id: this.categoryId || 0,
        name: formData.name,
        slug: formData.slug,
        imagePath: this.uploadFile.fileName,
        children: [],
        activeFlag: true,
        displayOrder: formData.sortOrder,
        isEditMode: false,
        imageFileData: this.uploadFile.fileData,
        imageType: this.uploadFile.fileType
      };

      console.log('Saving category:', parentData);

      this.adminService.saveCategory(parentData)
        .subscribe({
          next: (category) => {
            formData.parentCategoryId = category.id;
            this.categoryId = category.id;

            if (this.isEditMode) {
              console.log('Updating category:', { id: this.categoryId, ...formData });
              this.success = 'Category updated successfully!';
            } else {
              console.log('Creating new category:', formData);
              this.success = 'Category created successfully!';
            }
            
            this.isSaving = false;
            // Redirect after success
            setTimeout(() => {
              this.router.navigate(['/admin/categories']);
            }, 1500);
          },
          error: (error) => {
            console.error('Error saving parent category:', error);
            this.error = 'Failed to save parent category. Please try again.';
            this.isSaving = false;
            return;
          }
        });
    } else {
      console.log('Form is invalid');
      this.markFormGroupTouched();
    }
  }

  onCancel(): void {
    this.router.navigate(['/admin/categories']);
  }

  onImageChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.error = 'Please select a valid image file.';
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.error = 'Image size must be less than 5MB.';
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.imagePreview = e.target?.result as string;
        this.uploadFile.fileData = e.target?.result.toString();
        this.categoryForm.patchValue({ image: this.imagePreview });
        this.error = '';
      };
      reader.readAsDataURL(file);

      this.uploadFile.fileName = file.name;
      this.uploadFile.fileType = file.type;

      //this.getBase64(file);
    }
  }

  // onFileChange(files: FileList) {

  //   if (files.length > 0) {
  //     const file = files[0];
  //     const fileToUpload = file.name;

  //     this.uploadFile.fileName = file.name;
  //     this.uploadFile.fileType = file.type;

  //     this.getBase64(file);
  //   }
  // }

  // getBase64(file) {
  //   const reader = new FileReader();
  //   reader.readAsDataURL(file);
  //   reader.onload = () => {
  //     this.uploadFile.fileData = reader.result.toString();
  //   };
  //   reader.onerror = error => {
  //     console.log('Error: ', error);
  //     return null;
  //   };
  // }

  onRemoveImage(): void {
    this.imagePreview = null;
    this.categoryForm.patchValue({ imagePath: '' });
    
    // Reset file input
    const fileInput = document.getElementById('imageInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  // Subcategory modal management methods
  onAddSubcategory(): void {
    this.editingSubcategory = null;
    this.isEditingSubcategory = false;
    this.showSubcategoryModal = true;
  }

  onEditSubcategory(subcategory: Subcategory): void {
    this.editingSubcategory = subcategory;
    console.log('Editing subcategory:', subcategory);
    this.isEditingSubcategory = true;
    this.showSubcategoryModal = true;
  }

  onCloseSubcategoryModal(): void {
    this.showSubcategoryModal = false;
    this.editingSubcategory = null;
    this.isEditingSubcategory = false;
  }

  onSaveSubcategory(formData: SubcategoryFormData): void {
    console.log('onSaveSubcategory called with formData:', formData);
    if (this.isEditingSubcategory && this.editingSubcategory) {
      // Update existing subcategory
      const index = this.subcategories.findIndex(s => s.id === this.editingSubcategory!.id);
      if (index !== -1) {
        this.subcategories[index] = {
          ...this.editingSubcategory,
          id: this.editingSubcategory.id,
          name: formData.name,
          slug: formData.slug || this.generateSlug(formData.name),
          activeFlag: formData.activeFlag,
          updatedDate: this.subcategories[index].updatedDate || new Date(),
          createdDate: this.subcategories[index].createdDate,
          parentCategoryId: this.categoryId!
        };
        console.log('Subcategory updated:', this.subcategories[index]);
        this.success = 'Subcategory updated successfully!';
      }
    } else {
      // Add new subcategory
      const newSubcategory: Subcategory_v2 = {
        id: Math.max(...this.subcategories.map(s => s.id), 0) + 1,
        parentCategoryId: this.categoryId!,
        name: formData.name,
        slug: formData.slug || this.generateSlug(formData.name),
        activeFlag: formData.activeFlag,
        sortOrder: formData.sortOrder,
        createdDate: new Date(),
        updatedDate: new Date(),
        
      };
      this.subcategories.push(newSubcategory);
      console.log('New subcategory added:', newSubcategory);
      this.success = 'Subcategory added successfully!';
    }

    // Update category subcategory count
    if (this.category) {
      this.category.subcategoryCount = this.subcategories.length;
    }

    // Clear success message after 3 seconds
    setTimeout(() => {
      this.success = '';
    }, 3000);
  }

  onDeleteSubcategory(subcategory: Subcategory): void {

    if (!confirm(`Are you sure you want to delete the subcategory "${subcategory.name}"?`)) {
      return;
    }

    const mappedSubcategory: SubcategoryUpdate_v2 = {
        id: subcategory.id,
        parentCategoryId: this.categoryId,
        name: subcategory.name,
        slug: subcategory.slug || this.generateSlug(subcategory.name),
        activeFlag: false,
        sortOrder: subcategory.sortOrder
      };
      console.log('Deleting subcategory:', mappedSubcategory);
          
      this.categoryService.upsertSubcategory(this.categoryId, mappedSubcategory)
        .subscribe({
          next: (res) => {
            this.subcategories = this.subcategories.filter(s => s.id !== subcategory.id);
            console.log('Subcategory deleted:', subcategory.name);
            this.success = 'Subcategory deleted successfully!';
            
            // Update category subcategory count
            if (this.category) {
              this.category.subcategoryCount = this.subcategories.length;
            }
          },
          error: (err) => {
            console.error('Error updating subcategory:', err);
          }
        });

    // Clear success message after 3 seconds
    setTimeout(() => {
      this.success = '';
    }, 3000);
  }

  onToggleSubcategoryStatus(subcategory: Subcategory): void {
    console.log('Toggling status for subcategory:', subcategory);
    const index = this.subcategories.findIndex(s => s.id === subcategory.id);
    if (index !== -1) {
      this.subcategories[index].activeFlag = !this.subcategories[index].activeFlag;

      const request: ProductCategoryStatusRequest = {
        productCategoryId: this.subcategories[index].id,
        activeFlag: this.subcategories[index].activeFlag
      };

      this.adminService.setProductCategoryStatus(request)
        .subscribe({
          next: (data) => {
            this.subcategories[index].updatedDate = new Date();
            console.log(`Subcategory "${subcategory.name}" status changed to:`, 
              this.subcategories[index].activeFlag ? 'Active' : 'Inactive');
            this.success = `Subcategory ${this.subcategories[index].activeFlag ? 'activated' : 'deactivated'} successfully!`;
          },
          error: (err) => {
            console.error('Error updating subcategory:', err);
          }
        })
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        this.success = '';
      }, 3000);
    }
  }

  private generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  private markFormGroupTouched(): void {
    Object.keys(this.categoryForm.controls).forEach(key => {
      const control = this.categoryForm.get(key);
      control?.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.categoryForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldName: string): string {
    const field = this.categoryForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) return `${fieldName} is required.`;
      if (field.errors['minlength']) return `${fieldName} must be at least ${field.errors['minlength'].requiredLength} characters.`;
      if (field.errors['maxlength']) return `${fieldName} must not exceed ${field.errors['maxlength'].requiredLength} characters.`;
      if (field.errors['pattern']) return `${fieldName} contains invalid characters.`;
      if (field.errors['min']) return `${fieldName} must be at least ${field.errors['min'].min}.`;
    }
    return '';
  }

}